/**
 * RAVEN - Auction Tool: PackageList.jsx
 * [Presentational table/list view for all auction packages.]
 * Handles sorting, paging, selection, and triggers for edit/view actions.
 */

import React, { useState, useMemo, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

// Custom hooks
import { useListTableState } from "src/hooks/useListTableState";

// Layout/UI
import {
	Box,
	ButtonGroup,
	Checkbox,
	IconButton,
	Pagination,
	Switch,
	Table,
	Text,
	MetreaTooltip,
} from "@metrea-ui/v2";
import styles from "./PackageList.module.css";
import RecordNav from "../UI/RecordNav/RecordNav";
import { EnhancedTableHead, EnhancedTableToolbar } from "../UI/common/EnhancedTableParts";
import {
	ChevronLeftRegular,
	ChevronRightRegular,
	EditRegular,
	MoneyHandRegular,
	EyeRegular,
} from "@fluentui/react-icons";

// Utilities
import { getValuesByPropertyPath } from "src/utils/dataUtils";
import { Session, isDevTeam, isAdmin, isDeveloper, isStrat } from "src/utils/sessionUtils";
import { debugLog, showTesting, isDevSiteNow } from "src/utils/debugUtils";
import { getShortDateTimeString } from "src/utils/formatUtils";

/**
 * Main PackageList component
 * @param {object} props - { onViewPackage, onEditPackage, mode }
 * @returns {JSX.Element}
 */
const PackageList = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	// --- Redux Selectors ---
	const auctionPackages = useSelector((state) => state.auctions?.list ?? []);

	// --- Local State ---
	const [showInactive, setShowInactive] = useState(false);
	const [itemSelected, setItemSelected] = useState([]);

	useEffect(() => {
		debugLog(2, "[PackageList][FLOW] mounted", {});
	}, []);

	// Helper to return filtered auction packages based on Active/Inactive toggle
	const visiblePackages = useMemo(() => {
		return showInactive ? auctionPackages : auctionPackages.filter((x) => x.status_id === 1);
	}, [auctionPackages, showInactive]);

	// --- Controlled selection handler (notifies parent on user change only)
	const handleSelectionChange = (newSelected) => {
		debugLog(3, "[PackageList][ACTION] handleSelectionChange", {
			count: newSelected?.length ?? 0,
		});
		setItemSelected(newSelected);
	};

	// Helper: Is current user a bidder in this package?
	const isUserBidder = (item) => {
		const bidders = item.auc_package_bidders
			? getValuesByPropertyPath(item.auc_package_bidders, "user_id")
			: [];
		return bidders.indexOf(Session().currUser.id) !== -1;
	};

	const canManagePackage = (auctionPackage) => {
		if (!isStrat()) return false;
		if (isAdmin() || isDeveloper()) return true;
		return Number(auctionPackage?.createdBy) === Number(Session().currUser?.id);
	};

	// Custom hooks
	const {
		order,
		setOrder,
		orderBy,
		setOrderBy,
		setPage,
		pageM1,
		setPageM1,
		rowsPerPage,
		setRowsPerPage,
		dense,
		selected,
		visibleRows,
		emptyRows,
		handleRequestSort,
		handleChangePageM1,
		handleChangeDense,
		handleSelectAllClick,
		isSelected,
		checkHandler,
	} = useListTableState({
		items: visiblePackages, // Or from Redux selector
		initialOrderBy: "updatedAt",
		initialOrder: "desc",
		initialRowsPerPage: 10,
		initialPage: 1,
		selectionMode: true,
		selected: itemSelected,
		onSelectedChange: handleSelectionChange,
		//  Provide accessors for computed columns
		sortAccessors: {
			number_of_items: (row) => row?.auc_package_items?.length ?? 0,
			number_of_bidders: (row) => row?.auc_package_bidders?.length ?? 0,
		},
	});

	const headCells = [
		{
			id: "empty",
			numeric: false,
			disablePadding: false,
			label: " ",
			disableSort: true,
		},
		{
			id: "name",
			numeric: false,
			disablePadding: false,
			label: "Name",
		},
		{
			id: "description",
			numeric: false,
			disablePadding: false,
			label: "Description",
		},
		{
			id: "baseline",
			numeric: false,
			disablePadding: false,
			label: "Baseline",
		},
		{
			id: "number_of_items",
			numeric: true,
			disablePadding: true,
			label: "Item #",
			align: "center !important",
		},
		{
			id: "number_of_bidders",
			numeric: true,
			disablePadding: true,
			label: "Bidder #",
			align: "center !important",
		},
		{
			id: "updatedAt",
			numeric: false,
			disablePadding: false,
			label: "Last Updated",
		},
		{
			id: "status_id",
			numeric: false,
			disablePadding: false,
			label: "Status",
		},
	];
	// Development: Show UUID if testing
	if (showTesting() && isDevTeam()) {
		headCells.unshift({
			id: "uuid",
			numeric: false,
			disablePadding: false,
			label: "UUID",
		});
	}

	// --- Handlers ---
	const handleViewPackage = (event, id) => {
		// In-app open path standard: resolve package by numeric id.
		debugLog(3, "[PackageList][ACTION] handleViewPackage", { id: id });
		props.onViewPackage(id);
	};

	const handleEditPackage = (event, id) => {
		debugLog(3, "[PackageList][ACTION] handleEditPackage", { id: id });
		props.onEditPackage(id);
	};

	const toggleInactiveHandler = (option) => {
		debugLog(3, "[PackageList][ACTION] toggleInactiveHandler", { showInactive: option });
		setShowInactive(option);
	};

	const sortByHandler = (option) => {
		debugLog(3, "[PackageList][ACTION] sortByHandler", {
			orderBy: option.orderBy,
			order: option.order,
		});
		setOrderBy(option.orderBy);
		setOrder(option.order);
	};

	const changeRowsPerPageHandler = (option) => {
		debugLog(3, "[PackageList][ACTION] changeRowsPerPageHandler", { rowsPerPage: option.id });
		setRowsPerPage(option.id);
		setPage(0);
		setPageM1(1);
	};

	debugLog(9, "[PackageList][FLOW] render", {
		renderCount: renderCount.current,
		rowsPerPage: rowsPerPage,
		pageM1: pageM1,
		selectedCount: selected?.length ?? 0,
		visiblePackageCount: visiblePackages?.length ?? 0,
		showInactive: showInactive,
	});

	return (
		<Box w="100%">
			<Text
				as="div"
				style={{
					flex: "1 1 25%",
					paddingTop: "20px",
					fontSize: "18px",
					fontWeight: 400,
				}}
			>
				Auction Packages
			</Text>
			<RecordNav
				data={visiblePackages}
				showToggleInactive
				showSortBy
				showRowsPerPage
				rowsPerPage={rowsPerPage}
				count={visibleRows.length}
				onToggleInactive={toggleInactiveHandler}
				onSortBy={sortByHandler}
				onChangeRowsPerPage={changeRowsPerPageHandler}
			/>
			<Box w="100%" mb={2} boxShadow="sm" bg="white" rounded="md">
				{showTesting() && isDevTeam() && <EnhancedTableToolbar numSelected={selected.length} />}
				<Box overflowX="auto">
					<Table.Root size={dense ? "sm" : "md"} variant="outline" aria-labelledby="tableTitle">
						<EnhancedTableHead
							numSelected={selected.length}
							order={order}
							orderBy={orderBy}
							onSelectAllClick={handleSelectAllClick}
							onRequestSort={handleRequestSort}
							rowCount={visiblePackages.length}
							headCells={headCells}
							styles={styles}
						/>
						<Table.Body>
							{visibleRows.map((row) => {
								const isItemSelected = isSelected(row.id);

								return (
									<Table.Row
										aria-checked={isItemSelected}
										tabIndex={-1}
										key={row.id}
										selected={isItemSelected}
										// Keep non-clickable table text on the default arrow cursor; only the
										// action icons in this list are actionable, and plain text should not
										// fall back to the browser's text-selection I-beam.
										style={{ cursor: "default" }}
										className={styles.tableBodyRow}
									>
										{showTesting() && isDevTeam() && (
											<Table.Cell>
												<Checkbox.Root
													checked={isItemSelected}
													onCheckedChange={(next) =>
														checkHandler(
															{
																target: {
																	checked:
																		next === true ||
																		next?.checked === true ||
																		next?.detail?.checked === true,
																},
															},
															row.id,
														)
													}
													style={{ display: "inline-flex", alignItems: "center" }}
												>
													<Checkbox.HiddenInput />
													<Checkbox.Control />
												</Checkbox.Root>
											</Table.Cell>
										)}

										<Table.Cell>
											<span className="flex">
												{canManagePackage(row) && (
													<MetreaTooltip
														content={`Edit Package: ${row.name}`}
													>
														<span>
															<IconButton
																size="md"
																color="#3b6c83 !important"
																variant="ghost"
																onClick={(event) =>
																	handleEditPackage(event, row.id)
																}
															>
																<EditRegular />
															</IconButton>
														</span>
													</MetreaTooltip>
												)}
												<MetreaTooltip
													content={`${
														isUserBidder(row)
															? "Start Bidding"
															: "View Bidding Progress"
													} for Package: ${row.name}`}
												>
													<span>
														<IconButton
															size="md"
															color="#3b6c83 !important"
															variant="ghost"
															onClick={(event) =>
																handleViewPackage(event, row.id)
															}
														>
															{isUserBidder(row) ? (
																<MoneyHandRegular />
															) : (
																<EyeRegular />
															)}
														</IconButton>
													</span>
												</MetreaTooltip>
											</span>
										</Table.Cell>

										{showTesting() && isDevTeam() && <Table.Cell>{row.uuid}</Table.Cell>}
										<Table.Cell>{row.name}</Table.Cell>
										<Table.Cell>{row?.description}</Table.Cell>
										<Table.Cell>
											{row.baseline?.description?.trim?.() ? (
												<MetreaTooltip content={row.baseline.description}>
													{row.baseline?.name}
												</MetreaTooltip>
											) : (
												row.baseline?.name
											)}
										</Table.Cell>
										<Table.Cell style={{ textAlign: "center" }}>
											{row.auc_package_items?.length ?? 0}
										</Table.Cell>
										<Table.Cell style={{ textAlign: "center" }}>
											{row.auc_package_bidders?.length ?? 0}
										</Table.Cell>
										<Table.Cell style={{ textAlign: "left", fontSize: "0.7rem" }}>
											{getShortDateTimeString(row.updatedAt)}
										</Table.Cell>
										<Table.Cell>{row.status_id === 1 ? "Active" : "Inactive"}</Table.Cell>
									</Table.Row>
								);
							})}
							{emptyRows > 0 && (
								<Table.Row style={{ height: (dense ? 33 : 53) * emptyRows }}>
									<Table.Cell
										colSpan={(showTesting() && isDevTeam() ? 1 : 0) + headCells.length}
										style={{ borderBottom: "none", padding: 0 }}
									/>
								</Table.Row>
							)}
						</Table.Body>
					</Table.Root>
				</Box>
			</Box>

			<Box className="flex-auto justify-items-center p-1">
				<Pagination.Root
					count={visiblePackages.length}
					pageSize={Number(rowsPerPage) || 10}
					page={pageM1}
					onPageChange={({ page }) => handleChangePageM1(undefined, page)}
				>
					<ButtonGroup variant="ghost" size="2xs">
						<Pagination.PrevTrigger asChild>
							<IconButton>
								<ChevronLeftRegular />
							</IconButton>
						</Pagination.PrevTrigger>
						<Pagination.Items
							render={(page) => (
								<IconButton variant={{ base: "ghost", _selected: "solid" }}>
									{page.value}
								</IconButton>
							)}
						/>
						<Pagination.NextTrigger asChild>
							<IconButton>
								<ChevronRightRegular />
							</IconButton>
						</Pagination.NextTrigger>
					</ButtonGroup>
				</Pagination.Root>
			</Box>

			{showTesting() && isDevTeam() && isDevSiteNow() && (
				<Switch.Root
					size="sm"
					className="m-[10px]"
					checked={dense}
					onCheckedChange={(next) =>
						handleChangeDense({
							target: {
								checked:
									next === true || next?.checked === true || next?.detail?.checked === true,
							},
						})
					}
				>
					<Switch.HiddenInput />
					<Switch.Control>
						<Switch.Thumb />
					</Switch.Control>
					<Switch.Label>Dense padding</Switch.Label>
				</Switch.Root>
			)}
		</Box>
	);
};

PackageList.propTypes = {
	onViewPackage: PropTypes.func.isRequired,
	onEditPackage: PropTypes.func.isRequired,
	mode: PropTypes.string,
};

export default PackageList;
